// src/hooks/useFileUpload.ts
import { useState, useCallback, useRef } from 'react';
import { devLog } from '../utils/devTools';
import { generateThumbnail as generateDocumentThumbnail } from '../services/ThumbnailService';
import type { RemoteFileRef } from '../services/ThumbnailService';

interface UploadedFile {
  originalName: string;
  filename: string;
  mimetype: string;
  size: number;
  url?: string;
  uploaded?: boolean;
}

interface FileUploadState {
  isUploading: boolean;
  progress: number;
  error: string | null;
  uploadId?: string;  // Unique ID for tracking uploads
  thumbnailUrl?: string;  // Thumbnail URL for preview
  fileName?: string;  // Name of the file being uploaded
  totalSize?: number;  // Total size of all files in bytes
  uploadSpeed?: number; // Upload speed in bytes per second
  timeRemaining?: number; // Estimated time remaining in seconds
  bytesUploaded?: number; // Number of bytes uploaded so far
  bytesTotal?: number; // Total file size in bytes from XMLHttpRequest
}

interface FileUploadHook {
  uploadState: FileUploadState;
  uploadFiles: (
    files: FileList,
    token: string,
    fileType: string,
    onThumbnailGenerated?: (thumbnail: string) => void,
    deponentId?: string
  ) => Promise<{ success: boolean; files?: UploadedFile[]; thumbnails?: string[] }>;
  retryUpload: () => Promise<{ success: boolean; files?: UploadedFile[]; thumbnails?: string[] }>;
  cancelUpload: () => void;
  resetUploadState: () => void;
  generateThumbnail: (file: File | RemoteFileRef | null) => Promise<string>;
}

/**
 * Hook for handling file uploads with progress tracking, error handling, and thumbnail generation
 * @returns File upload methods and state
 */
export const useFileUpload = (): FileUploadHook => {
  const [uploadState, setUploadState] = useState<FileUploadState>({
    isUploading: false,
    progress: 0,
    error: null,
    uploadId: undefined,
    thumbnailUrl: undefined,
    fileName: undefined,
    totalSize: undefined,
    uploadSpeed: undefined,
    timeRemaining: undefined
  });
  
  // Keep reference to the current upload for retry/cancel functionality
  const currentUploadRef = useRef<{
    xhr?: XMLHttpRequest;
    files?: FileList;
    token?: string;
    fileType?: string;
    onThumbnailGenerated?: (thumbnail: string) => void;
    deponentId?: string;
  }>({});

  const resetUploadState = useCallback(() => {
    setUploadState({
      isUploading: false,
      progress: 0,
      error: null,
      uploadId: undefined,
      thumbnailUrl: undefined,
      fileName: undefined,
      totalSize: undefined,
      uploadSpeed: undefined,
      timeRemaining: undefined,
      bytesUploaded: undefined,
      bytesTotal: undefined
    });
    
    // Clear the current upload reference
    currentUploadRef.current = {};
  }, []);

  /**
   * Generate a thumbnail for the given file
   * @param file File to generate thumbnail for
   * @returns Promise resolving to the data URL of the thumbnail
   */
  const generateThumbnail = useCallback(async (file: File | RemoteFileRef | null): Promise<string> => {
    try {
      // Make sure file is valid
      if (!file) {
        console.error('generateThumbnail received null or undefined file');
        return '/file-icons/generic.svg';
      }
      const ref = file as RemoteFileRef;
      devLog('generateThumbnail called with file:', {
        isFile: file instanceof File,
        isBlob: file instanceof Blob,
        name: file.name || ref.originalName || ref.filename,
        type: file instanceof File ? file.type : ref.mimetype,
        size: file.size,
        hasUrl: !!ref.url,
        fileKeys: Object.keys(file)
      });

      // Use the new thumbnail service which handles all file types
      return await generateDocumentThumbnail(file);
    } catch (outerError) {
      console.error('Critical error in generateThumbnail:', outerError);
      return '/file-icons/generic.svg';
    }
  }, []);

  /**
   * Cancel the current upload if it's in progress
   */
  const cancelUpload = useCallback(() => {
    if (currentUploadRef.current.xhr && uploadState.isUploading) {
      currentUploadRef.current.xhr.abort();
      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        error: 'Upload cancelled'
      }));
    }
  }, [uploadState.isUploading]);

  /**
   * Retry the last upload attempt
   */
  const retryUpload = useCallback(async (): Promise<{ success: boolean; files?: UploadedFile[]; thumbnails?: string[] }> => {
    const { files, token, fileType, onThumbnailGenerated, deponentId } = currentUploadRef.current;

    if (!files || !token || !fileType) {
      return { success: false };
    }

    // Reset error state but keep upload ID for tracking
    setUploadState(prev => ({
      ...prev,
      isUploading: true,
      progress: 0,
      error: null
    }));

    return uploadFilesInternal(files, token, fileType, onThumbnailGenerated, deponentId);
  // ESLINT NOTE: uploadFilesInternal is defined later in the file and is a stable useCallback([generateThumbnail])
  // reference. Adding it here would require reordering declarations; the closure captures by reference
  // so retryUpload always calls the current implementation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Internal implementation of file upload that both uploadFiles and retryUpload use
   */
  const uploadFilesInternal = useCallback(
    async (
      files: FileList,
      token: string,
      fileType: string,
      onThumbnailGenerated?: (thumbnail: string) => void,
      deponentId?: string
    ): Promise<{ success: boolean; files?: UploadedFile[]; thumbnails?: string[] }> => {
      if (!files.length) {
        return { success: false };
      }

      const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      // Format filename to preserve extension if truncated in the UI
      let fileName = '';
      if (files.length === 1) {
        const fullName = files[0].name;
        // If filename might be long, prepare it for potential truncation
        if (fullName.length > 25) {
          const lastDotIndex = fullName.lastIndexOf('.');
          if (lastDotIndex !== -1) {
            const extension = fullName.substring(lastDotIndex);
            const baseName = fullName.substring(0, lastDotIndex);
            // Store with a special separator that the UI can use to preserve extension
            fileName = `${baseName}[...]${extension}`;
          } else {
            fileName = fullName;
          }
        } else {
          fileName = fullName;
        }
      } else {
        fileName = `${files.length} files`;
      }
      const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
      
      setUploadState(prev => ({
        ...prev,
        isUploading: true,
        progress: 0,
        error: null,
        uploadId,
        fileName,
        totalSize
      }));

      // Extract the core fileType without any prefixes
      // Make sure we're only using the base fileType for server validation
      const coreFileType = fileType.includes('_') 
        ? fileType.split('_').pop() || fileType 
        : fileType;
      
      const formData = new FormData();
      formData.append('fileType', coreFileType);
      
      devLog(`Creating form with fileType=${coreFileType}, original=${fileType}`);

      // Generate thumbnails in parallel while uploading files
      const thumbnailPromises: Promise<string>[] = [];
      
      for (let i = 0; i < files.length; i++) {
        // Ensure we're appending actual File objects and not serialized objects
        if (files[i] instanceof File) {
          formData.append('files', files[i]);
          // Generate thumbnails for each file
          thumbnailPromises.push(generateThumbnail(files[i]));
        } else {
          console.error('Invalid file object at index', i, files[i]);
          // Skip this file or try to convert it
        }
      }

      try {
        // Start thumbnail generation in parallel with upload
        const thumbnailsPromise = Promise.all(thumbnailPromises);
        
        // Use XMLHttpRequest to track progress
        const uploadPromise = new Promise<{ success: boolean; files?: UploadedFile[] }>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          
          // Store the XHR instance for retry/cancel
          currentUploadRef.current.xhr = xhr;
          
          // Add debugging: Log the form data being sent
          devLog("FormData contents before upload:");
          // Use Array.from to avoid TypeScript iteration issues
          Array.from(formData.entries()).forEach(pair => {
            const [key, value] = pair;
            devLog(`${key}: ${value instanceof File ? value.name : value}`);
          });

          // Track upload metrics for time estimation
          const uploadMetrics = {
            startTime: Date.now(),
            lastProgressTime: Date.now(),
            lastLoaded: 0,
            uploadSpeed: 0, // bytes per second
            timeRemaining: 0, // seconds
            hasReceivedProgress: false // Flag to track if we've received any real progress events
          };

          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progressPercent = Math.round((event.loaded / event.total) * 100);
              const now = Date.now();
              
              // Mark that we've received a real progress event
              uploadMetrics.hasReceivedProgress = true;
              
              // Calculate upload speed and time remaining if we have enough data
              if (now - uploadMetrics.lastProgressTime > 500 && event.loaded > uploadMetrics.lastLoaded) {
                const timeElapsed = (now - uploadMetrics.lastProgressTime) / 1000; // in seconds
                const bytesDelta = event.loaded - uploadMetrics.lastLoaded;
                
                // Calculate current upload speed (moving average)
                const currentSpeed = bytesDelta / timeElapsed; // bytes per second
                uploadMetrics.uploadSpeed = uploadMetrics.uploadSpeed === 0 
                  ? currentSpeed 
                  : (uploadMetrics.uploadSpeed * 0.7) + (currentSpeed * 0.3); // Weighted average
                
                // Calculate remaining time with sanity checks
                const bytesRemaining = event.total - event.loaded;
                
                // Prevent division by zero or very small numbers
                if (uploadMetrics.uploadSpeed < 100) { // Less than 100 bytes/sec is suspiciously slow
                  uploadMetrics.timeRemaining = 60; // Default to 1 minute if speed is unreliable
                } else {
                  // Calculate time but cap it to reasonable limits
                  const calculatedTime = bytesRemaining / uploadMetrics.uploadSpeed;
                  // Cap maximum estimated time to 2 hours (7200 seconds)
                  uploadMetrics.timeRemaining = Math.min(7200, calculatedTime);
                }
                
                // Update last metrics
                uploadMetrics.lastProgressTime = now;
                uploadMetrics.lastLoaded = event.loaded;
              }
              
              // Only start simulating progress after we've received at least one real progress update
              // or after a reasonable timeout (3 seconds) with no progress
              const elapsedTime = (now - uploadMetrics.startTime) / 1000; // seconds
              const shouldUseSimulation = !uploadMetrics.hasReceivedProgress && elapsedTime > 3;
              
              // Use real progress when available
              let effectiveProgress = progressPercent;
              
              // If no real progress events are firing, use simulation
              if (progressPercent === 0 && shouldUseSimulation) {
                // Start with a conservative estimate for the simulation
                let simulatedProgress = 0;
                
                if (uploadMetrics.timeRemaining > 0) {
                  // Simulate progress based on time elapsed, but cap it appropriately
                  simulatedProgress = Math.min(
                    // Cap at 90% to avoid showing completion before it's done
                    90,
                    // For large files, start very conservatively
                    Math.min(30, Math.round((elapsedTime / (elapsedTime + uploadMetrics.timeRemaining)) * 100))
                  );
                } else {
                  // If we can't calculate timeRemaining yet, use a very conservative progress estimation
                  // Start at 1% and grow very slowly
                  simulatedProgress = Math.min(15, Math.round(elapsedTime));
                }
                
                effectiveProgress = simulatedProgress;
              }
              
              setUploadState(prev => ({
                ...prev,
                progress: effectiveProgress,
                uploadSpeed: uploadMetrics.uploadSpeed,
                timeRemaining: uploadMetrics.timeRemaining,
                fileName,
                bytesUploaded: event.loaded,
                bytesTotal: event.total
              }));
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              setUploadState(prev => ({
                ...prev,
                isUploading: false,
                progress: 100,
                error: null
              }));
              try {
                const response = JSON.parse(xhr.responseText);
                // If the response has a url property, preserve it, otherwise use storagePath
                if (response.files && Array.isArray(response.files)) {
                  response.files = response.files.map((file: UploadedFile) => {
                    if (file.url) {
                      console.warn('Direct file URL detected in response.');
                    }
                    // Ensure file has proper metadata for client-side tracking
                    if (!file) {
                      console.error('Null or undefined file in server response:', response);
                      // Create minimal valid file data to prevent errors
                      return {
                        originalName: 'unknown',
                        mimetype: 'application/octet-stream',
                        size: 0,
                        filename: 'unknown',
                        uploaded: true
                      };
                    }
                    return {
                      ...file,
                      uploaded: true
                    };
                  });
                } else {
                  console.error('Server response missing files array:', response);
                  // Ensure we always have a valid files array
                  response.files = [];
                }
                resolve(response);
              } catch (e) {
                reject(new Error('Invalid response format'));
              }
            } else {
              let errorMessage = `Upload failed: ${xhr.statusText}`;
              try {
                const response = JSON.parse(xhr.responseText);
                if (response.error) {
                  errorMessage = response.error;
                }
              } catch (e) {
                // Use default error message if response is not JSON
              }
              
              setUploadState(prev => ({
                ...prev,
                isUploading: false,
                progress: 0,
                error: errorMessage
              }));
              reject(new Error(errorMessage));
            }
          });

          xhr.addEventListener('error', () => {
            setUploadState(prev => ({
              ...prev,
              isUploading: false,
              progress: 0,
              error: 'Upload failed due to network error'
            }));
            reject(new Error('Network error'));
          });

          xhr.addEventListener('abort', () => {
            setUploadState(prev => ({
              ...prev,
              isUploading: false,
              progress: 0,
              error: 'Upload aborted'
            }));
            reject(new Error('Upload aborted'));
          });

          // Use the appropriate API URL including the base URL
          const API_URL = window.APP_CONFIG?.apiUrl || '';
          
          // Build the URL with query parameters
          let url = `${API_URL}/api/uploads/${token}?fileType=${encodeURIComponent(coreFileType)}`;
          
          // Add deponentId as a query parameter if available
          if (deponentId) {
            url += `&deponentId=${encodeURIComponent(deponentId)}`;
          }
          
          xhr.open('POST', url);
          xhr.withCredentials = true; // Send the reporter session cookie, if any, for log traceability
          xhr.send(formData);
        });

        // Wait for both upload and thumbnails
        const [uploadResult, thumbnails] = await Promise.all([
          uploadPromise,
          thumbnailsPromise
        ]);
        
        // Set first thumbnail and call callback if provided
        if (thumbnails.length > 0) {
          setUploadState(prev => ({
            ...prev,
            thumbnailUrl: thumbnails[0]
          }));
          
          if (onThumbnailGenerated) {
            onThumbnailGenerated(thumbnails[0]);
          }
        }

        // Log the result to debug the response structure
        devLog("Upload result:", uploadResult);

        return { 
          ...uploadResult, 
          thumbnails 
        };
      } catch (error) {
        setUploadState(prev => ({
          ...prev,
          isUploading: false,
          progress: 0,
          error: error instanceof Error ? error.message : 'Unknown error'
        }));
        throw error;
      }
    },
    [generateThumbnail]
  );

  /**
   * Upload files to the server with progress tracking and thumbnail generation
   * @param files Files to upload
   * @param token Authentication token
   * @param fileType Type of file being uploaded
   * @param onThumbnailGenerated Optional callback for when thumbnail is generated
   * @returns Result of the upload
   */
  const uploadFiles = useCallback(
    async (
      files: FileList,
      token: string,
      fileType: string,
      onThumbnailGenerated?: (thumbnail: string) => void,
      deponentId?: string
    ): Promise<{ success: boolean; files?: UploadedFile[]; thumbnails?: string[] }> => {
      // Store the parameters for retry capability
      currentUploadRef.current = {
        files,
        token,
        fileType,
        onThumbnailGenerated,
        deponentId
      };
      
      return uploadFilesInternal(files, token, fileType, onThumbnailGenerated, deponentId);
    },
    [uploadFilesInternal]
  );

  return {
    uploadState,
    uploadFiles,
    retryUpload,
    cancelUpload,
    resetUploadState,
    generateThumbnail
  };
};